import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Optional,
} from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Marks the host's form control as touched once focus genuinely leaves the
 * host element — not while it moves between the host's own inner inputs.
 *
 * Card #11408. A composite control such as the ngx-bootstrap `<timepicker>`
 * renders Hours and Minutes as two separate `<input>`s and never calls the
 * `onTouched` callback it registers with Angular (`registerOnTouched` stores
 * the callback and nothing invokes it). It does call `onChange(null)` from
 * both sub-inputs while the value is still incomplete, which marks the
 * control `dirty` mid-entry. So `dirty` cannot tell "the admin is still
 * typing" from "the admin left this incomplete", and `touched` is never set
 * at all; the only reliable signal is focus leaving the whole widget.
 *
 * `focusout` is used rather than `blur` because it bubbles up from the inner
 * inputs to this host; `blur` does not bubble and would never fire here.
 *
 * `focusout` alone is not enough, which is what the three guards below are
 * for: a `focusout` whose `relatedTarget` is null means "focus went somewhere
 * this event will not name", and on the browsers that matter here that is
 * raised by cases which are emphatically *not* the admin leaving the field.
 */
@Directive({
  selector: '[appTouchOnLeave]',
})
export class TouchOnLeaveDirective implements OnDestroy {
  /**
   * Set by a pointer press that landed inside the host, and consumed by the
   * `focusout` that press provokes.
   *
   * Safari — and therefore the Capacitor iOS WebView, which runs these admin
   * screens — does not focus the picker's `<a href="javascript:void(0);">`
   * spinner arrows when they are clicked. It blurs the Hours/Minutes input
   * with a null `relatedTarget` and leaves focus on the document body, which
   * no after-the-fact DOM check can tell apart from the admin clicking away
   * to a non-focusable part of the page. The press itself is the only
   * evidence that the admin is still working inside this very picker, so it
   * has to be remembered when it happens.
   */
  private pointerLandedInsideHost = false;

  private pendingFocusOwnerCheck: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    // Optional so that putting this on an element with no form control — easy
    // to do when a sixth screen picks the directive up — is inert rather than
    // a NullInjectorError at construction.
    @Optional() private readonly ngControl: NgControl | null,
  ) {}

  ngOnDestroy(): void {
    this.cancelPendingFocusOwnerCheck();
  }

  @HostListener('pointerdown', ['$event'])
  @HostListener('mousedown', ['$event'])
  onPointerDown(event: Event): void {
    const target = event.target as Node | null;
    this.pointerLandedInsideHost =
      !!target && this.host.nativeElement.contains(target);
  }

  /**
   * Entering the picker starts a fresh interaction, so no earlier press of
   * ours is still relevant. Without this, a press that moved focus *into* the
   * picker from another field (whose `focusout` fires on that field, not on
   * this host, and so never consumes the flag) would leave the flag standing
   * and suppress the message on the admin's next genuine exit.
   */
  @HostListener('focusin')
  onFocusIn(): void {
    this.pointerLandedInsideHost = false;
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const pressedInsideHost = this.pointerLandedInsideHost;
    this.pointerLandedInsideHost = false;

    const nextFocusTarget = event.relatedTarget as Node | null;
    if (nextFocusTarget) {
      if (!this.host.nativeElement.contains(nextFocusTarget)) {
        this.markTouched();
      }
      // Hours → Minutes, or a box → one of the picker's own spinner arrows.
      // The admin is still inside the field, so this is not leaving it.
      return;
    }

    if (pressedInsideHost) {
      // One of this picker's own arrows, on a browser that blurs rather than
      // focuses it. Showing the required message here would be exactly the
      // mid-entry message #11408 exists to remove.
      return;
    }

    this.scheduleFocusOwnerCheck();
  }

  /**
   * With no `relatedTarget` and no press of our own to go on, let the browser
   * settle and then ask who actually owns focus. Focus having moved back into
   * the picker, or never having left it at all — the whole window lost focus
   * because the admin switched apps or tabs mid-entry — is not leaving the
   * field either.
   */
  private scheduleFocusOwnerCheck(): void {
    if (this.pendingFocusOwnerCheck !== null) {
      return;
    }
    this.pendingFocusOwnerCheck = setTimeout(() => {
      this.pendingFocusOwnerCheck = null;
      const focusOwner = this.host.nativeElement.ownerDocument?.activeElement;
      if (focusOwner && this.host.nativeElement.contains(focusOwner)) {
        return;
      }
      this.markTouched();
    });
  }

  private cancelPendingFocusOwnerCheck(): void {
    if (this.pendingFocusOwnerCheck !== null) {
      clearTimeout(this.pendingFocusOwnerCheck);
      this.pendingFocusOwnerCheck = null;
    }
  }

  private markTouched(): void {
    this.ngControl?.control?.markAsTouched();
  }
}
